import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Linkedin, Calendar } from "lucide-react";
import albert from "@/assets/team/albert-saunders.jpeg";
import uche from "@/assets/team/uche-obi.jpeg";
import antwon from "@/assets/team/antwon-salisbury.jpeg";
import zaron from "@/assets/team/zaron-johnson.jpeg";
import thomas from "@/assets/team/thomas-wilkes.jpeg";
import nick from "@/assets/team/nick-eaton.jpeg";
import aaron from "@/assets/team/aaron-herring.jpeg";
import kenon from "@/assets/team/kenon-lattimore.jpeg";
import steve from "@/assets/team/steve-griffin.png";
import ashli from "@/assets/team/ashli-saunders.png";
import albertSr from "@/assets/team/albert-saunders-sr.png";

interface Member {
  name: string;
  title: string;
  image: string;
  bio?: string;
  booking: string;
  linkedin?: string;
}

const DEFAULT_BOOKING = "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics";

const team: Member[] = [
  {
    name: "Albert O'Neil Saunders Jr.",
    title: "Founder & CEO",
    image: albert,
    bio: "AI Automation Visionary, Robotics Innovator, and Business Transformation Leader.",
    booking: DEFAULT_BOOKING,
  },
  {
    name: "Bishop Albert Saunders Sr.",
    title: "Chief Financial Officer",
    image: albertSr,
    bio: "Executive Leadership Advisor & Kingdom Business Strategist. Decades of leadership in business, ministry, operations, and community development — guiding Neil AI's financial stewardship, growth, and long-term strategic vision alongside Founder & CEO Albert O'Neil Saunders Jr.",
    booking: DEFAULT_BOOKING,
  },
  {
    name: "Uche J. Obi",
    title: "Chief Operating Officer",
    image: uche,
    bio: "12+ years across financial services, automotive, commercial operations, and logistics. Translates complex AI & robotics into clear business outcomes.",
    booking: "https://api.leadconnectorhq.com/widget/bookings/uche-obi-personal-calendar-kvvpufn1m",
  },
  {
    name: "Steve Griffin",
    title: "VP of Sales · Head of Robotics",
    image: steve,
    bio: "Leads enterprise robotics sales and deployment strategy across the Neil AI portfolio.",
    booking: DEFAULT_BOOKING,
  },
  {
    name: "Ashli Saunders",
    title: "Chief Office Administrator",
    image: ashli,
    bio: "Oversees daily operations, client coordination, and executive administration at Neil AI Global Solutions.",
    booking: DEFAULT_BOOKING,
  },
  {
    name: "Kenon Lattimore",
    title: "Chief of Staff",
    image: kenon,
    booking: "https://api.leadconnectorhq.com/widget/bookings/kenon-lattimore-personal-calendar-xmfjnkrgr",
  },
  {
    name: "Nick Eaton",
    title: "Technology Specialist · Certified PUDU Tech",
    image: nick,
    booking: "https://api.leadconnectorhq.com/widget/bookings/nick-eaton-personal-calendar-ehsslmzzo",
  },
  {
    name: "Antwon Salisbury",
    title: "Sr. Business Transformation Advisor",
    image: antwon,
    booking: "https://api.leadconnectorhq.com/widget/bookings/salisbury-antwon-business-calendar-neilai",
  },
  {
    name: "Zaron Johnson",
    title: "Sr. Business Transformation Advisor",
    image: zaron,
    booking: "https://api.leadconnectorhq.com/widget/bookings/zaron-johnson-personal-calendar-vu7dtiom_",
  },
  {
    name: "Thomas Wilkes",
    title: "Sr. Business Transformation Advisor",
    image: thomas,
    booking: "https://api.leadconnectorhq.com/widget/bookings/thomas-wilkes-personal-calendar-us4ixzc4z",
  },
  {
    name: "Aaron Herring",
    title: "Sr. Business Transformation Advisor",
    image: aaron,
    booking: DEFAULT_BOOKING,
  },
];

const TeamCard = ({ member }: { member: Member }) => (
  <Card
    className="group relative flex flex-col h-full overflow-hidden border border-[hsl(var(--line))] bg-gradient-to-b from-black/70 to-black/40 backdrop-blur-sm transition-all duration-500 hover:border-primary/50 hover:-translate-y-1"
    style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}
  >
    {/* Top gold accent */}
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[hsl(43_75%_55%)] to-transparent opacity-60" />

    {/* Portrait — fixed aspect for consistency */}
    <div className="relative aspect-[4/5] overflow-hidden">
      <img
        src={member.image}
        alt={`${member.name} – ${member.title} at Neil AI Global Solutions`}
        loading="lazy"
        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
      />
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 30%, hsl(43 75% 55% / 0.18), transparent 60%)" }}
      />
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/90 to-transparent" />
    </div>

    {/* Info */}
    <div className="flex flex-col flex-1 p-4 md:p-5 gap-3">
      <div className="min-h-[64px]">
        <h3 className="font-orbitron font-bold text-[15px] md:text-base text-foreground leading-snug line-clamp-2">
          {member.name}
        </h3>
        <p className="text-[11px] md:text-xs text-primary font-semibold mt-1.5 leading-snug line-clamp-2">
          {member.title}
        </p>
      </div>

      {member.bio ? (
        <p className="text-[12px] text-muted-foreground leading-relaxed line-clamp-3">{member.bio}</p>
      ) : (
        <div className="flex-1" />
      )}

      <div className="flex gap-2 pt-1 mt-auto">
        <Button
          asChild
          size="sm"
          className="flex-1 h-8 px-3 text-[11px] font-semibold rounded-full bg-primary/90 hover:bg-primary text-primary-foreground"
        >
          <a href={member.booking} target="_blank" rel="noopener noreferrer">
            <Calendar className="w-3 h-3 mr-1" />
            Book
          </a>
        </Button>
        <Button
          asChild
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0 rounded-full border-[hsl(var(--line))] hover:bg-primary/10 hover:border-primary/50"
          aria-label={`${member.name} on LinkedIn`}
        >
          <a href={member.linkedin || "#"} target="_blank" rel="noopener noreferrer">
            <Linkedin className="w-3.5 h-3.5" />
          </a>
        </Button>
      </div>
    </div>
  </Card>
);

const TeamSection = () => {
  return (
    <section id="team" className="py-20 md:py-28 scroll-mt-32">
      <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto px-4">
        <p className="text-[11px] md:text-xs font-bold tracking-[0.3em] text-primary mb-4">
          MEET THE TEAM
        </p>
        <h2 className="font-orbitron font-bold text-3xl md:text-5xl mb-5 text-foreground leading-tight">
          The People Building the <span className="text-primary">Future of Business Operations</span>
        </h2>
        <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
          Meet the team helping organizations reduce burnout, modernize workflows, and scale
          intelligently through AI and robotics.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {team.map((m) => (
          <TeamCard key={m.name} member={m} />
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-14">
        <Button
          asChild
          size="lg"
          className="h-11 px-7 rounded-full bg-gradient-to-r from-[hsl(43_75%_55%)] to-[hsl(43_85%_65%)] text-black font-bold tracking-wide shadow-[0_4px_24px_hsl(43_75%_55%/0.35)] hover:opacity-95"
        >
          <a href={DEFAULT_BOOKING} target="_blank" rel="noopener noreferrer">
            Book a Consultation With Our Team
          </a>
        </Button>
        <Button
          asChild
          size="lg"
          variant="outline"
          className="h-11 px-7 rounded-full border-2 border-primary/50 text-primary hover:bg-primary/10 hover:border-primary font-bold tracking-wide"
        >
          <a href="/#team">Meet the Full Team</a>
        </Button>
      </div>
    </section>
  );
};

export default TeamSection;
